/**
 * S-C Scorecard — the screen that says the system is not working.
 *
 * 🔴 **It is a top-level nav entry, and that is the whole argument for it.** The screen that says
 * the system is failing must not be reachable only *through* the screen that says it is working.
 * `API-and-UI.md` makes the same argument the UI spec made for putting Wallet at top level.
 *
 * ## The hero is negative and stays negative
 *
 * `−1.4%` renders at display size in `--loss`, with no softening, no rolling window chosen after
 * the fact, and no "since inception" reframing. `n` sits beside it because an excess return over
 * 23 picks is not evidence and the screen must not let it read as though it were.
 *
 * ⚠ **An em dash at a horizon means the horizon has not arrived — it does not mean zero.** Every
 * pick published is listed, including the two published this morning whose 1-week mark is eleven
 * weeks away. Dropping unmarked picks would turn the table into a survivor's list, which is the
 * single easiest way to make a scorecard lie without writing a false number.
 *
 * ## Operator entries are a separate line, not merged into the sample
 *
 * The four Trading 212 positions are real money, entered by hand, and they are **not** the system's
 * published picks. Adding them to `n` would mix a pre-registered sample with discretionary entries
 * — the exact contamination the pre-registration block below exists to prevent. They get their own
 * panel, their own `n`, and the dotted broker marker.
 */

import type { JSX } from 'react';

import { Num } from '../../design/components/data/Num.jsx';
import { MODE_LABELS, clockOf, useTimestampMode } from '../../ui/timestamps';
import { useIsPhone } from '../../ui/useIsPhone';
import { asPercent } from './format';
import { BrokerPill } from './glyphs';
import type { Pick, Scorecard } from './model';

/** The three horizons, in the order they arrive. Fixed by the pre-registration, not by the UI. */
const HORIZONS = [1, 4, 12] as const;

export function ScorecardScreen({ card }: { card: Scorecard }): JSX.Element {
  const mode = useTimestampMode();
  // 🔴 **Rendered, not hidden.** `useIsPhone`'s own docstring records the three CSS attempts that
  // failed to remove a column from a table; the same is true of a seven-track grid. See below for
  // why this screen in particular could not be left as a horizontal scroller.
  const phone = useIsPhone();

  return (
    <div className="jmt-screen jmt-screen--capped jmt-stk-scorecard">
      <header>
        <p className="jmt-stk-panel-label">EXCESS RETURN</p>
        <p className="jmt-stk-hero__num">
          <Num value={asPercent(card.excessPct)} kind="percent" dp={1} step="display" arrow />
        </p>
        <p className="jmt-stk-hero__sub">
          excess vs {card.benchmark} · n={card.n} · {card.window} · all picks, no window chosen
          after the fact
        </p>
      </header>

      <section className="jmt-panel jmt-panel--flush">
        <h2 className="jmt-panel__title jmt-stk-panel-head__solo">Every pick published</h2>
        {phone ? (
          card.picks.map((pick) => <PhonePick key={`${pick.ticker}-${pick.publishedOn}`} pick={pick} />)
        ) : (
          <div className="jmt-stk-scroll">
            <div className="jmt-stk-table jmt-stk-table--picks">
              <div className="jmt-stk-table__head">
                <span>TICKER</span>
                <span>PUBLISHED</span>
                <span data-num>AT PICK</span>
                <span data-num>FAMILY</span>
                {HORIZONS.map((weeks) => (
                  <span data-num key={weeks}>
                    {weeks}W
                  </span>
                ))}
              </div>
              {card.picks.map((pick) => (
                <div className="jmt-stk-table__row" key={`${pick.ticker}-${pick.publishedOn}`}>
                  <span>
                    <code>{pick.ticker}</code>
                  </span>
                  <span data-quiet>
                    <code>{pick.publishedOn}</code>
                  </span>
                  <span data-num>{pick.priceAtPick}</span>
                  <span data-num className="jmt-stk-family">
                    {pick.family}
                  </span>
                  {HORIZONS.map((weeks) => {
                    const mark = pick.marks.find((entry) => entry.weeks === weeks);
                    return (
                      <span data-num key={weeks}>
                        <Num value={asPercent(mark?.excessPct)} kind="percent" dp={1} />
                      </span>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="jmt-panel__foot">
          Marks are excess over {card.benchmark} at the horizon. An em dash means the horizon has
          not arrived, not zero.
        </p>
      </section>

      <section className="jmt-panel jmt-panel--flush">
        <h2 className="jmt-panel__title jmt-stk-panel-head__solo">Attribution by family</h2>
        {card.families.map((family) => (
          <div className="jmt-stk-family-row" key={family.name}>
            <span className="jmt-stk-family">{family.name}</span>
            <span className="jmt-stk-family-row__note">{family.note}</span>
            {/* `n` beside every family, because `GOVT` at +4.1% over three names is the number
                most likely to be read as a result, and it is the one least entitled to be. */}
            <span className="jmt-stk-family-row__n">n={family.n}</span>
            <span className="jmt-stk-family-row__val">
              <Num value={asPercent(family.excessPct)} kind="percent" dp={1} />
            </span>
          </div>
        ))}
      </section>

      <section className="jmt-panel jmt-stk-operator">
        <div className="jmt-stk-operator__head">
          <h2 className="jmt-panel__title">Operator entries</h2>
          <BrokerPill />
        </div>
        {/* 🔴 Absent is said, not zeroed. `0 positions · — excess · n=0` reads as a measurement
            of nothing when the truth is that `/stocks/scorecard` does not carry this block yet. */}
        {card.operator ? (
          <p className="jmt-stk-operator__body">
            Trading 212, entered by hand, tracked separately from published picks:{' '}
            {card.operator.positions} positions ·{' '}
            <Num
              value={asPercent(card.operator.excessPct)}
              kind="percent"
              dp={1}
              arrow
              step="sm"
            />{' '}
            excess vs {card.benchmark} · n={card.operator.positions} · positions read at{' '}
            {clockOf(card.operator.readAt, mode)} {MODE_LABELS[mode]}.
          </p>
        ) : (
          <p className="jmt-stk-operator__body">
            Not measured. The broker positions are read each morning for the held-name verbs, but
            their performance is not yet tracked against the benchmark separately from the published
            picks — <code>/api/v1/stocks/scorecard</code> does not carry this block.
          </p>
        )}
      </section>

      {/* 🔴 On the page rather than in a document, so the goalposts are readable next to the
          result. A pre-registration nobody can see beside the number it constrains is a
          pre-registration that can be quietly revised. */}
      <section className="jmt-stk-prereg">
        {card.preRegistration ? (
          <>
            <p className="jmt-stk-panel-label">
              PRE-REGISTRATION · FILED {card.preRegistration.filedOn}, BEFORE THE FIRST PICK
            </p>
            <p className="jmt-stk-prose">{card.preRegistration.claim}</p>
          </>
        ) : (
          <>
            <p className="jmt-stk-panel-label">PRE-REGISTRATION · NOT SERVED BY THE API</p>
            {/* ⚠ The claim itself is real and was filed before the first pick; it lives in the
                vault. What is missing is the endpoint that would put it beside the number, which is
                the whole point of printing it here — so the gap is named rather than papered over. */}
            <p className="jmt-stk-prose">
              Filed in <code>03-Architecture/Stocks/Digest-and-Scorecard.md</code> before the first
              pick. It is not on the wire, so it cannot be shown next to the result it constrains —
              which is what this panel exists to do.
            </p>
          </>
        )}
      </section>

    </div>
  );
}

/**
 * One pick on a phone: identity, then **the three marks side by side under their horizons**.
 *
 * 🔴 **The seven-column table could not survive as a horizontal scroller here, and this screen is
 * the reason the rule is not "wrap it in `overflow-x: auto` and move on".** At 360px the scroller
 * showed `TICKER` and `PUBLISHED` and nothing else — so the Scorecard, whose entire job is to say
 * whether the picks beat the benchmark, showed *which names were picked and when* and hid every
 * number that answers the question. A screen that reports the system's failures must not be the
 * one that hides them on the device the operator actually reads it on.
 *
 * The marks keep their own row rather than being folded into a sentence, because three signed
 * percentages under `1W / 4W / 12W` is a shape the eye compares down a list; prose is not.
 */
function PhonePick({ pick }: { pick: Pick }): JSX.Element {
  return (
    <div className="jmt-stk-pick">
      <p className="jmt-stk-pick__head">
        <code className="jmt-stk-pick__t">{pick.ticker}</code>
        <span className="jmt-stk-pick__price">{pick.priceAtPick}</span>
      </p>
      <p className="jmt-stk-pick__meta">
        <span className="jmt-stk-family">{pick.family}</span>
        <code>{pick.publishedOn}</code>
      </p>
      <div className="jmt-stk-pick__marks">
        {HORIZONS.map((weeks) => {
          const mark = pick.marks.find((entry) => entry.weeks === weeks);
          return (
            <div key={weeks}>
              <span className="jmt-stk-pick__h">{weeks}W</span>
              <Num value={asPercent(mark?.excessPct)} kind="percent" dp={1} step="sm" />
            </div>
          );
        })}
      </div>
    </div>
  );
}
